import { adminApiUrl } from '@/lib/adminApi';

/**
 * Client for the support-admin payment bridge, api-py's
 * `/api/admin/v2/users/{profileId}/payments*` and `/delete*` routes (ClickUp
 * 869f0ev24 / 869f0ev30). Same-origin, `credentials: 'include'` carries the
 * `ff_admin_session` cookie exactly as adminApi.ts's calls do.
 *
 * Every write call requires a non-empty `reason` at the TYPE level (no
 * optional field a caller could accidentally omit) — the API enforces this
 * too (400 without it), but the button-level dialogs in the UI are the first
 * place a missing reason should be caught.
 */

/**
 * What the two support actions actually did (ClickUp 869f353ff). payment-back
 * names the rows it acted on, and an empty list means it acted on nothing —
 * the console reports that as "nothing to do" rather than as a deletion, which
 * is what the old fixed-string receipt did while the action was dead.
 */
export interface DeleteTokenResult {
  ok: true;
  deleted: string[];
}

export interface CancelSubscriptionResult {
  ok: true;
  cancelled: string[];
}

export interface PaymentHistoryRow {
  kind: string;
  provider: string;
  provider_ref: string;
  effect: Record<string, unknown> | null;
  processed_at: string;
}

export interface PaymentsResponse {
  organization_id: string | null;
  history: PaymentHistoryRow[];
  available: boolean;
}

export interface DeletePreview {
  profile_id: string;
  email: string | null;
  platform_role: string | null;
  primary_organization_id: string | null;
  organizations_to_be_deleted: string[];
  blocked_by_sole_admin_of_org: string | null;
  has_payment_history: boolean;
  payment_tokens_will_be_requested_deleted_via: string | null;
  third_party_targets: string[];
}

export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(adminApiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) {
    throw new AdminApiError(res.status, payload.error ?? `request failed: ${res.status}`);
  }
  return payload;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(adminApiUrl(path), { credentials: 'include' });
  const payload = (await res.json().catch(() => ({}))) as { error?: string } & T;
  if (!res.ok) {
    throw new AdminApiError(res.status, payload.error ?? `request failed: ${res.status}`);
  }
  return payload;
}

export function fetchPayments(profileId: string): Promise<PaymentsResponse> {
  return getJson(`/api/admin/v2/users/${profileId}/payments`);
}

export function deletePaymentToken(profileId: string, reason: string): Promise<DeleteTokenResult> {
  return postJson(`/api/admin/v2/users/${profileId}/payments/delete-token`, { reason });
}

/**
 * Cancels every active subscription of this user's organization. There is no
 * scope argument: payment-back's per-tier cancels (premium/vip) filter by
 * product benefit type, a concept of the donor catalog, and this catalog holds
 * zero benefit rows on dev and on prod — a tier cancel could only ever match
 * nothing, so the API refuses those scopes.
 */
export function cancelSubscription(
  profileId: string,
  reason: string,
): Promise<CancelSubscriptionResult> {
  return postJson(`/api/admin/v2/users/${profileId}/payments/cancel-subscription`, { reason });
}

export function refundOrder(
  profileId: string,
  orderId: string,
  reason: string,
): Promise<{ ok: true }> {
  return postJson(`/api/admin/v2/users/${profileId}/payments/refund-order/${orderId}`, {
    reason,
  });
}

export function previewDeleteAccount(profileId: string, reason: string): Promise<DeletePreview> {
  return postJson(`/api/admin/v2/users/${profileId}/delete/preview`, { reason });
}

export function deleteAccount(
  profileId: string,
  reason: string,
  confirm: string,
  mode?: 'delete' | 'tombstone',
): Promise<{ ok: true; outcome: string; orgs_deleted: string[] }> {
  return postJson(`/api/admin/v2/users/${profileId}/delete`, { reason, confirm, mode });
}
